import React, { useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import ColorPickerField from './ColorPickerField';

// Define the template variables
const TEMPLATE_VARIABLES = [
  { key: 'startDate', label: 'Start Date', description: 'Flyer start date (e.g., "Mar 14, 2025")' },
  { key: 'endDate', label: 'End Date', description: 'Flyer end date (e.g., "Mar 16, 2025")' },
  { key: 'flyerTitle', label: 'Flyer Title', description: 'Title of the flyer' },
  { key: 'effectiveDates', label: 'Effective Dates', description: 'Full date range (e.g., "Mar 14 to 16")' },
  { key: 'storeAddress', label: 'Store Address', description: 'Full store address' },
  { key: 'storePhone', label: 'Store Phone', description: 'Store phone number' }
];

const TextProperties = ({ shape, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Function to insert a variable at cursor position or append to the end
  const insertVariable = (variableKey) => {
    const placeholder = `{{${variableKey}}}`;
    const newText = shape.text + placeholder;

    onChange({
      ...shape,
      text: newText,
      hasVariables: true
    });
    handleMenuClose();
  };

  const handleWidthChange = (event) => {
    onChange({ ...shape, width: parseInt(event.target.value) });
  };

  const handleTextChange = (event) => {
    onChange({ ...shape, text: event.target.value });
  };

  const handleFontFamilyChange = (event) => {
    onChange({ ...shape, fontFamily: event.target.value });
  };

  const handleFontSizeChange = (event) => {
    const newFontSize = Math.max(parseInt(event.target.value), 5);
    onChange({ ...shape, fontSize: newFontSize });
  };

  const handleFontStyleChange = (event) => {
    onChange({ ...shape, fontStyle: event.target.value });
  };

  const handleTextDecorationChange = (event) => {
    onChange({ ...shape, textDecoration: event.target.value });
  };

  const handleAlignChange = (event) => {
    onChange({ ...shape, align: event.target.value });
  };

  const handleVerticalAlignChange = (event) => {
    onChange({ ...shape, verticalAlign: event.target.value });
  };

  const handlePaddingChange = (event) => {
    onChange({ ...shape, padding: parseInt(event.target.value) });
  };

  const handleLineHeightChange = (event) => {
    onChange({ ...shape, lineHeight: parseInt(event.target.value) });
  };

  const handleWrapChange = (event) => {
    onChange({ ...shape, wrap: event.target.value });
  };

  const handleEllipsisChange = (event) => {
    onChange({ ...shape, ellipsis: event.target.value });
  };

  const handleColorChange = (color) => {
    onChange({ ...shape, fill: color });
  };

  const handleShadowColorChange = (color) => {
    onChange({ ...shape, shadowColor: color });
  };

  const handleShadowBlurChange = (event) => {
    onChange({ ...shape, shadowBlur: parseInt(event.target.value) });
  };

  const handleShadowOffsetXChange = (event) => {
    onChange({ ...shape, shadowOffsetX: parseInt(event.target.value) });
  };

  const handleShadowOffsetYChange = (event) => {
    onChange({ ...shape, shadowOffsetY: parseInt(event.target.value) });
  };

  const handleLinkedProductNumberChange = (event) => {
    const newLinkedProductNumber = event.target.value;
    onChange({ ...shape, linkedProductNumber: newLinkedProductNumber.toString() });
  };

  const handleLinkedProductPropertyChange = (event) => {
    const newLinkedProductProperty = event.target.value;
    onChange({ ...shape, linkedProductProperty: newLinkedProductProperty });
  };

  return (
    <div className="shape-specific-properties">
      <div className="shape-property">
        <label className="shape-property-label">Text</label>
        <textarea value={shape.text || ''} onChange={handleTextChange} style={{width: '150px', height: '24px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Variable</label>
        <Button
          onClick={handleMenuClick}
          sx={{
            backgroundColor: '#ff8264',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            cursor: 'pointer',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: 'auto',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#4e4ba0'
            }
          }}
        >
          <KeyboardArrowDown fontSize="small" />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
        >
          {TEMPLATE_VARIABLES.map(variable => (
            <MenuItem
              key={variable.key}
              onClick={() => insertVariable(variable.key)}
              sx={{ fontSize: '14px' }}
            >
              {variable.label} - {variable.description}
            </MenuItem>
          ))}
        </Menu>
      </div>
      <ColorPickerField
        label="Text Color"
        color={shape.fill || '#000000'}
        onChange={handleColorChange}
      />
      <ColorPickerField
        label="Shadow Color"
        color={shape.shadowColor || '#000000'}
        onChange={handleShadowColorChange}
      />
      <div className="shape-property">
        <label className="shape-property-label">Width</label>
        <input type="number" min="40" max="1000" value={shape.width || ''} onChange={handleWidthChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Font</label>
        <input type="text" value={shape.fontFamily || ''} onChange={handleFontFamilyChange} style={{width: '80px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Size</label>
        <select
          value={shape.fontSize || 5}
          onChange={handleFontSizeChange}
          style={{width: '60px'}}
        >
          {[...Array(196)].map((_, index) => (
            <option key={index} value={index + 5}>
              {index + 5}
            </option>
          ))}
        </select>
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Style</label>
        <input type="text" value={shape.fontStyle || ''} onChange={handleFontStyleChange} style={{width: '60px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Decoration</label>
        <input type="text" value={shape.textDecoration || ''} onChange={handleTextDecorationChange} style={{width: '60px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Align</label>
        <input type="text" value={shape.align || ''} onChange={handleAlignChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">V Align</label>
        <input type="text" value={shape.verticalAlign || ''} onChange={handleVerticalAlignChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Padding</label>
        <input type="number" value={shape.padding || ''} onChange={handlePaddingChange} style={{width: '40px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Line H</label>
        <input type="number" value={shape.lineHeight || ''} onChange={handleLineHeightChange} style={{width: '40px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Wrap</label>
        <input type="text" value={shape.wrap || ''} onChange={handleWrapChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Ellipsis</label>
        <input type="text" value={shape.ellipsis || ''} onChange={handleEllipsisChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Shadow Blur</label>
        <input type="number" value={shape.shadowBlur} onChange={handleShadowBlurChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Shadow X</label>
        <input type="number" value={shape.shadowOffsetX} onChange={handleShadowOffsetXChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Shadow Y</label>
        <input type="number" value={shape.shadowOffsetY} onChange={handleShadowOffsetYChange} style={{width: '50px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Linked Product</label>
        <input type="number" min="1" max="10" value={shape.linkedProductNumber || ''} onChange={handleLinkedProductNumberChange} style={{width: '40px'}} />
      </div>
      <div className="shape-property">
        <label className="shape-property-label">Linked Property</label>
        <select value={shape.linkedProductProperty || ''} onChange={handleLinkedProductPropertyChange} style={{width: '130px'}}>
          <option value="product_name">Product Name</option>
          <option value="product_description">Product Description</option>
          <option value="price_unit">Price Unit</option>
          <option value="original_price">Original Price</option>
          <option value="promo_price_dollars">Promo Price Dollar Amount</option>
          <option value="promo_price_cents">Promo Price Cents Amount</option>
          <option value="promo_start_date">Promo Start Date</option>
          <option value="promo_end_date">Promo End Date</option>
          <option value="savings">Savings</option>
          <option value="limited_qty">Limited Quantity</option>
          <option value="promo_type">Promo Type</option>
          <option value="inventory">Inventory</option>
          <option value="category">Category</option>
          <option value="sub_category_1">Sub Category 1</option>
          <option value="sub_category_2">Sub Category 2</option>
          <option value="sub_category_3">Sub Category 3</option>
        </select>
      </div>
    </div>
  );
};

export default TextProperties;
